<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { HeaderWrapper } from '@haskoning/vue-component-library'
import { useProjectUserStore } from '@/stores/projectUser'
import { useProjectStore } from '@/stores/project'
import { useDrawerStore } from '@/stores/drawer'
import { useProductStore } from '@/stores/product'
import { useResultStore } from '@/stores/result'
import { useCalculationStore } from '@/stores/calculation'
import { useBuildingBlockStore } from '@/stores/buildingBlock'
import { useActivityStore } from '@/stores/activity'
import { useMaterialStore } from '@/stores/material'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import { useEnvironmentDatabaseStore } from '@/stores/environmentDatabase'
import { useQuantityCodeStore } from '@/stores/quantityCode'
import { useTransportConfigStore } from '@/stores/transportConfig'

const authStore = useDjangoAuthStore()
const router = useRouter()
const route = useRoute()
const drawerStore = useDrawerStore()

const {
  writeLoading: projectWriteLoading,
  deleteLoading: projectDeleteLoading,
  currentItem: currentProject
} = storeToRefs(useProjectStore())

const { writeLoading: projectUserWriteLoading, deleteLoading: projectUserDeleteLoading } =
  storeToRefs(useProjectUserStore())

const {
  writeLoading: calculationWriteLoading,
  currentItem: currentCalculationItem,
  deleteLoading: calculationDeleteLoading
} = storeToRefs(useCalculationStore())

const { writeLoading: buildingBlockWriteLoading, deleteLoading: buildingBlockDeleteLoading } =
  storeToRefs(useBuildingBlockStore())

const { writeLoading: activityWriteLoading, deleteLoading: activityDeleteLoading } =
  storeToRefs(useActivityStore())

const { writeLoading: materialWriteLoading, deleteLoading: materialDeleteLoading } =
  storeToRefs(useMaterialStore())

const { writeLoading: productWriteLoading, deleteLoading: productDeleteLoading } =
  storeToRefs(useProductStore())

const { writeLoading: resultWriteLoading, deleteLoading: resultDeleteLoading } =
  storeToRefs(useResultStore())

const { writeLoading: quantityBBWriteLoading, deleteLoading: quantityBBDeleteLoading } =
  storeToRefs(useQuantityBuildingBlockStore())

const { writeLoading: envDbWriteLoading, deleteLoading: envDbDeleteLoading } = storeToRefs(
  useEnvironmentDatabaseStore()
)

const { writeLoading: quantityCodeWriteLoading, deleteLoading: quantityCodeDeleteLoading } =
  storeToRefs(useQuantityCodeStore())

const { writeLoading: transportConfigWriteLoading, deleteLoading: transportConfigDeleteLoading } =
  storeToRefs(useTransportConfigStore())

const user = computed(() => authStore.user)

const loading = computed(() => {
  return (
    projectWriteLoading.value ||
    projectDeleteLoading.value ||
    projectUserWriteLoading.value ||
    projectUserDeleteLoading.value ||
    calculationWriteLoading.value ||
    calculationDeleteLoading.value ||
    buildingBlockWriteLoading.value ||
    buildingBlockDeleteLoading.value ||
    activityWriteLoading.value ||
    activityDeleteLoading.value ||
    materialWriteLoading.value ||
    materialDeleteLoading.value ||
    productWriteLoading.value ||
    productDeleteLoading.value ||
    resultWriteLoading.value ||
    resultDeleteLoading.value ||
    quantityBBWriteLoading.value ||
    quantityBBDeleteLoading.value ||
    envDbWriteLoading.value ||
    envDbDeleteLoading.value ||
    quantityCodeWriteLoading.value ||
    quantityCodeDeleteLoading.value ||
    transportConfigWriteLoading.value ||
    transportConfigDeleteLoading.value
  )
})

const currentRouteName = computed(() => route.name)

const isSidebarPage = computed(() => {
  const routeName = currentRouteName.value
  return (
    routeName === 'calculation-index' ||
    routeName === 'user-management' ||
    routeName === 'calculation-form'
  )
})

async function logout() {
  authStore.logout()
  await router.push({ name: 'auth-login' })
}
</script>

<template>
  <HeaderWrapper v-if="user" title="SIM" :user="user" :loading="loading" @logout="logout">
    <template v-if="isSidebarPage" #toolbar-prepend>
      <v-app-bar-nav-icon
        class="pl-3 no-overlay"
        height="36"
        width="40"
        color="white"
        :ripple="false"
        :icon="drawerStore.drawer.shown ? 'mdi-close' : 'mdi-menu'"
        @click="drawerStore.toggleDrawer"
      />
    </template>
    <template #breadcrumbs-items>
      <!-- My Projects -->
      <v-breadcrumbs-item>
        <router-link
          class="text-white toolbar-text text-decoration-none"
          :to="{ name: 'project-index' }"
        >
          My Projects
        </router-link>
      </v-breadcrumbs-item>
      <!-- Project Name -->
      <template v-if="currentProject?.id && $route.name !== 'project-index'">
        <v-breadcrumbs-divider />
        <v-breadcrumbs-item>
          <router-link
            style="max-width: 150px"
            class="text-white text-truncate toolbar-text text-decoration-none"
            :to="{ name: 'project-detail', params: { id: currentProject.id } }"
          >
            {{ currentProject?.name }}
          </router-link>
        </v-breadcrumbs-item>
      </template>
      <!-- Calculation Name -->
      <template v-if="currentCalculationItem?.id && $route.name !== 'project-detail'">
        <v-breadcrumbs-divider />
        <v-breadcrumbs-item>
          <span
            style="max-width: 100px"
            class="text-white text-truncate toolbar-text text-decoration-none"
          >
            {{ currentCalculationItem?.name }}
          </span>
        </v-breadcrumbs-item>
      </template>
    </template>
  </HeaderWrapper>
</template>
